import React from 'react';
import { Link } from 'react-router-dom';

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-bg text-white">
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-2 mb-6"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Refund Policy</h1>
          <p className="text-white/60 text-sm">
            Last updated: January 12, 2026
          </p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-white/80">
          <p>
            This Refund Policy explains how refunds work for subscriptions and purchases made on GenAnything ("GenAnything", "we", "us", or "our"). By purchasing a subscription or credits, you agree to this policy.
          </p>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Overview</h2>
            <p>
              GenAnything provides subscription-based access to AI-powered image and video generation features. Because our services involve immediate access to digital functionality and usage-based resources (credits), all purchases are generally non-refundable, except where required by law or explicitly stated in this policy.
            </p>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Subscriptions</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Billing</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscriptions are billed on a monthly or yearly basis, depending on the plan selected.</li>
              <li>Charges are processed securely through Stripe.</li>
              <li>By subscribing, you authorize us to charge your selected payment method on a recurring basis until you cancel.</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Cancellation</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may cancel your subscription at any time through the Stripe Customer Portal or your account dashboard.</li>
              <li>Cancellation takes effect at the end of the current billing period.</li>
              <li>You will continue to have access to your subscription benefits until the billing period ends.</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Refunds for Subscriptions</h3>
            <p>Subscription fees are non-refundable, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Partial billing periods</li>
              <li>Unused time</li>
              <li>Downgrades during an active billing cycle</li>
            </ul>
            <p className="mt-4">We do not provide prorated refunds when you cancel or change plans.</p>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Credits & Usage</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Credit Allocation</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Each subscription plan includes a fixed number of credits per billing cycle.</li>
              <li>Credits reset at the start of every new billing cycle to the amount included in your active plan.</li>
              <li>Unused credits do not roll over to the next billing cycle.</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Credit Usage</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Credits are consumed when you generate images or videos.</li>
              <li>Once credits are used, they cannot be restored or refunded.</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">Credit Purchases / Top-Ups</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Any one-time credit purchases or top-ups are non-refundable.</li>
              <li>Credits are delivered instantly and are considered consumed once available in your account.</li>
            </ul>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Refund Exceptions</h2>
            <p>Refunds may be issued only at our discretion and typically only in cases of:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Duplicate charges</li>
              <li>Billing errors caused by a technical issue on our end</li>
              <li>Unauthorized charges (subject to investigation)</li>
            </ul>
            <p className="mt-4">
              If you believe you were charged incorrectly, please contact us promptly.
            </p>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Chargebacks & Disputes</h2>
            <p>Initiating a chargeback or payment dispute through your bank or Stripe without contacting us first may result in:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Suspension or termination of your account</li>
              <li>Loss of access to remaining credits or subscription features</li>
            </ul>
            <p className="mt-4">
              We strongly encourage you to contact us so we can resolve billing issues directly.
            </p>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Legal Compliance</h2>
            <p>
              Nothing in this policy affects your statutory rights under applicable consumer protection laws. In jurisdictions where refunds are legally required, we will comply with applicable regulations.
            </p>
          </section>

          <hr className="border-white/20 my-8" />

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
            <p>
              If you have questions about this Refund Policy or believe you are eligible for a refund, please contact us at:
            </p>
            <p className="mt-4">
              <strong>Email:</strong>{' '}
              <a href="mailto:hello@genanything.ai" className="text-blue-400 hover:text-blue-300 underline">
                hello@genanything.ai
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

